import type {
  AffectedNode,
  ImpactAnswer,
  Node,
  NodeKind,
} from '@/shared/api/generated/lineage';

/* Turning two impact answers into a laid-out graph.
 *
 * **No graph library, and the reason is that the layout is already decided by the server.** The brief
 * says to use one if the project has it, otherwise the smallest appropriate dependency, and not to
 * build a fake SVG that cannot scale. What makes a lineage graph hard is normally *layout* — deciding
 * where nodes go — and that is what force-directed libraries exist for. Here `AffectedNode` carries
 * `depth`, so the x position of every node is given: this is a layered DAG whose layer index arrives on
 * the wire. What remains is arithmetic — stack each layer vertically and centre it — and a `<g
 * transform>` for pan and zoom.
 *
 * A library would have added 40–120 KB to an air-gapped wheelhouse to run a simulation whose answer we
 * already have, and force layout would actively make it *worse*: a lineage reader needs upstream on the
 * left and downstream on the right, and a physics simulation does not guarantee that. This is not a
 * freeform node editor; nothing is dragged, nothing is connected by hand.
 *
 * **What bounds it is the API rather than a client-side cap.** `ImpactAnswer` carries `visible_count`,
 * `complete` and `reason` — the server already refuses to walk past its own limits and says so — so the
 * node count is bounded before it reaches here. The screen renders that incompleteness rather than
 * hiding it, which is the whole reason those fields exist.
 */

export interface LaidOutNode {
  readonly id: string;
  readonly label: string;
  readonly kind: NodeKind;
  /** Negative upstream, 0 the subject, positive downstream. The layer index. */
  readonly depth: number;
  readonly x: number;
  readonly y: number;
  /** True for the node the reader asked about. */
  readonly subject: boolean;

  /** Which connection owns this node, or `null` when there is none to name.
   *
   * **A dataset's identity is `(connection, schema, object)`, so a box labelled only
   * `ANALYTICS.CUSTOMER` is ambiguous** — a prod and a dev Snowflake, or a Snowflake and a
   * Databricks table of the same name, are different assets that drew identically. It arrives on
   * the wire with the node rather than being resolved here, so the graph cannot show a name and a
   * system that came from two different reads.
   *
   * `null` for a job and for a dataset no harvest has catalogued. Rendered as absence rather than
   * as a placeholder, because "no connection" is a fact about those two and an unnamed badge would
   * be invented.
   */
  readonly connection: { readonly name: string; readonly platform: string } | null;
}

export interface LaidOutEdge {
  readonly from: string;
  readonly to: string;
}

export interface GraphLayout {
  readonly nodes: readonly LaidOutNode[];
  readonly edges: readonly LaidOutEdge[];
  readonly width: number;
  readonly height: number;
}

/** Geometry. Exported so the SVG and the hit-testing cannot disagree about a node's box. */
export const NODE_WIDTH = 168;
/* **52 again, and the reversal is deliberate rather than a drift back.** It was cut to 40 when the
 * *kind* moved out of the box and into the legend, and that was right: a second text line spending a
 * third of the box to restate one of three values the legend already names is waste.
 *
 * The second line now carries the node's **connection**, which the legend cannot say once because it
 * differs per node — and without it two boxes reading `ANALYTICS.CUSTOMER` in two different systems
 * are indistinguishable, on the graph whose whole subject is where data came from. Same pixels, and
 * the content is the opposite kind: a per-node fact rather than repeated vocabulary.
 *
 * The cost is real and is the fan-out case: six children each need 12px more vertical room. That is
 * the trade — an ambiguous graph fits more of itself on screen. */
export const NODE_HEIGHT = 52;
export const LAYER_GAP = 96;
export const ROW_GAP = 24;
const PADDING = 32;

/**
 * Lay out the subject with its provenance to the left and its impact to the right.
 *
 * **Edges are inferred from adjacent depths, and that is a stated approximation.** `ImpactAnswer` gives
 * nodes with depths and not the edges between them — the edge list for a subgraph is not a thing the
 * API returns. So a node at depth 2 is drawn as fed by every node at depth 1. For a linear pipeline,
 * which is what most lineage is, that is exactly right; for a wide fan-in it draws more connectors than
 * exist. It is the honest kind of wrong: it never invents a *node*, never hides one, and never claims a
 * dependency across a depth it did not observe.
 *
 * **`useNodeEdges` answers precisely and no screen calls it.** It is one node's real neighbours,
 * straight from `lineage.edge` — the exact answer this approximation is not — and the panel that read
 * it was removed when `/lineage` was cut back to the canvas alone. So the approximation is currently
 * the only thing a reader sees, which is why it is stated here and rendered nowhere as certainty.
 */
/** The wire's connection reference, narrowed to what a node draws.
 *
 * The generated type is `ConnectionRef | null | undefined` — optional *and* nullable, because the
 * contract marks it non-required with a null member. Both mean the same thing here (no connection to
 * name), so they collapse, written out rather than with `== null` so the collapse is a visible
 * decision instead of a loose comparison somebody later "tightens" into a bug. Same treatment
 * `confidencePercent` gives the three wire states of `confidence`.
 */
function connectionOf(
  reference: { readonly name: string; readonly platform: string } | null | undefined,
): { readonly name: string; readonly platform: string } | null {
  if (reference === null || reference === undefined) return null;
  return { name: reference.name, platform: reference.platform };
}

export function layOut(
  subject: Node,
  provenance: readonly AffectedNode[],
  impact: readonly AffectedNode[],
): GraphLayout {
  /* Depths are signed here: the API reports both directions as positive distances, which would stack
   * an upstream and a downstream node in the same column. */
  const byDepth = new Map<number, LaidOutNode[]>();
  const push = (depth: number, node: Omit<LaidOutNode, 'x' | 'y'>): void => {
    const layer = byDepth.get(depth) ?? [];
    layer.push({ ...node, x: 0, y: 0 });
    byDepth.set(depth, layer);
  };

  push(0, {
    id: subject.id,
    label: subject.display_name,
    kind: subject.kind,
    depth: 0,
    subject: true,
    connection: connectionOf(subject.connection),
  });
  for (const node of provenance) {
    push(-node.depth, {
      id: node.node_id,
      label: node.display_name,
      kind: node.kind,
      depth: -node.depth,
      subject: false,
      connection: connectionOf(node.connection),
    });
  }
  for (const node of impact) {
    push(node.depth, {
      id: node.node_id,
      label: node.display_name,
      kind: node.kind,
      depth: node.depth,
      subject: false,
      connection: connectionOf(node.connection),
    });
  }

  const depths = [...byDepth.keys()].sort((a, b) => a - b);
  /* The tallest layer sets the canvas height; every other layer is centred against it, so a chain with
   * one wide fan-out does not sit against the top edge. */
  const tallest = Math.max(...[...byDepth.values()].map((layer) => layer.length), 1);
  const height = PADDING * 2 + tallest * NODE_HEIGHT + (tallest - 1) * ROW_GAP;
  const width =
    PADDING * 2 + depths.length * NODE_WIDTH + (depths.length - 1) * LAYER_GAP;

  const placed: LaidOutNode[] = [];
  depths.forEach((depth, column) => {
    const layer = byDepth.get(depth) ?? [];
    const layerHeight = layer.length * NODE_HEIGHT + (layer.length - 1) * ROW_GAP;
    const top = (height - layerHeight) / 2;
    layer.forEach((node, row) => {
      placed.push({
        ...node,
        x: PADDING + column * (NODE_WIDTH + LAYER_GAP),
        y: top + row * (NODE_HEIGHT + ROW_GAP),
      });
    });
  });

  /* Connectors between adjacent *populated* depths, not adjacent integers. A gap in the depths — which
   * happens when the server truncated a layer — would otherwise draw nothing across it and make the
   * graph look like two unrelated components. */
  const edges: LaidOutEdge[] = [];
  for (let index = 0; index < depths.length - 1; index += 1) {
    const left = byDepth.get(depths[index] ?? 0) ?? [];
    const right = byDepth.get(depths[index + 1] ?? 0) ?? [];
    for (const from of left) {
      for (const to of right) {
        edges.push({ from: from.id, to: to.id });
      }
    }
  }

  return { nodes: placed, edges, width, height };
}

/** Which systems a graph spans, and how many of its nodes belong to none.
 *
 * **§21's requirement, expressed as a statement rather than as a filter.** The brief asks that a
 * connection filter never hide a cross-connection dependency, and the strongest way to hold that is
 * not to offer hiding at all: lineage is one graph, so a Databricks silver table feeding a Snowflake
 * mart is the answer rather than noise to be filtered out. What a reader needs instead is to be told
 * that the picture crosses systems — otherwise two boxes from two connections read as one estate.
 *
 * `unnamed` is counted separately and never folded into a "system" of its own. A job and an
 * unharvested table both belong to no connection, and inventing a bucket called "unknown" would put
 * a system badge on nodes that have none.
 */
export function systemsIn(layout: GraphLayout): {
  readonly names: readonly string[];
  readonly unnamed: number;
} {
  const names = new Set<string>();
  let unnamed = 0;
  for (const node of layout.nodes) {
    if (node.connection === null) unnamed += 1;
    else names.add(`${node.connection.platform} · ${node.connection.name}`);
  }
  return { names: [...names].sort(), unnamed };
}

/**
 * What a selected node relates to: everything before it, everything after it, and its siblings.
 *
 * **Siblings are the only genuinely unrelated nodes here, and that bound is set by the inferred edges
 * rather than chosen.** The first version returned upstream and downstream by depth comparison and left
 * the caller to dim the rest — which dimmed *nothing*, because in a layered graph every node is either
 * before or after every other, so "unrelated" was always the empty set. Measured in a browser on a
 * nine-node chain: selecting any node dimmed zero.
 *
 * Distinguishing a *branch* — telling `orders` that `customer_master` is not its ancestor — needs the
 * real edge list, and `ImpactAnswer` does not carry one (see `layOut`). So the honest highlight is the
 * one the data supports: a node at the same depth is provably neither an ancestor nor a descendant, and
 * gets dimmed. Everything on either side stays lit because it genuinely may be related.
 *
 * `useNodeEdges` is what answers precisely for one node, and **no screen offers it** any more — see
 * `layOut` above. A reader who needs to know whether a specific neighbour is real has no surface for
 * it today; the route is there and unconsumed.
 */
export function relatedTo(
  layout: GraphLayout,
  id: string,
): {
  readonly upstream: ReadonlySet<string>;
  readonly downstream: ReadonlySet<string>;
  readonly siblings: ReadonlySet<string>;
} {
  const selected = layout.nodes.find((node) => node.id === id);
  if (selected === undefined) {
    return { upstream: new Set(), downstream: new Set(), siblings: new Set() };
  }
  return {
    upstream: new Set(
      layout.nodes.filter((node) => node.depth < selected.depth).map((node) => node.id),
    ),
    downstream: new Set(
      layout.nodes.filter((node) => node.depth > selected.depth).map((node) => node.id),
    ),
    siblings: new Set(
      layout.nodes
        .filter((node) => node.depth === selected.depth && node.id !== selected.id)
        .map((node) => node.id),
    ),
  };
}

/** Whether either direction was truncated by the server, and why. Rendered, never hidden. */
export interface Completeness {
  readonly complete: boolean;
  readonly upstreamReason: string | null;
  readonly downstreamReason: string | null;
}

export function completenessOf(
  provenance: ImpactAnswer | undefined,
  impact: ImpactAnswer | undefined,
): Completeness {
  const up = provenance?.complete === false ? (provenance.reason ?? 'unknown') : null;
  const down = impact?.complete === false ? (impact.reason ?? 'unknown') : null;
  return {
    complete: up === null && down === null,
    upstreamReason: up,
    downstreamReason: down,
  };
}

/* A colour per node kind, from the categorical tier. Kinds are the platform's own vocabulary, so a
 * new member is a compile error here rather than a node that renders in the default colour
 * and looks like every other one.
 *
 * **These were `--accent-text`, `--status-info` and `--status-unknown`, and the three of them were
 * one colour.** They resolved to #0b4451, #1f5f7a and #5f6c74 - three dark blue-greys separating by
 * 8.7 in OKLab against a floor of 15. On an eight-pixel dot that is not a legend, and it was
 * reported as one: "Dataset, Column, Job is not differentiated properly".
 *
 * **The mistake was borrowing, and the test below did not catch it because it checked names.** It
 * asserted the three tokens were distinct, which they were; nothing asserted the *pixels* were.
 * `--accent-text` is chosen to sit under body copy and `--status-*` to be read one badge at a time,
 * so neither tier was ever asked whether its members were distinguishable from each other - that is
 * not the job either of them has. `--series-*` is a tier whose only job is identity, validated over
 * all pairs in both themes; `tokens.css` carries the measurements.
 *
 * Slot order is the colourblind-safety mechanism rather than a preference. Dataset takes slot 1
 * because it is the entity the screen is about, and column and job take 2 and 3; reordering them
 * means re-running the validator. */
export const KIND_COLOUR = {
  dataset: 'var(--series-1)',
  column: 'var(--series-2)',
  job: 'var(--series-3)',
} as const satisfies Record<NodeKind, string>;
